import { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FiMenu, FiX, FiBell, FiUser, FiLogOut, FiSettings, FiBriefcase, FiHeart } from 'react-icons/fi';
import { logout } from '@/context/slices/authSlice';
import './Navbar.scss';

// Wizard Logo SVG Component
const WizardLogo = () => (
  <svg width="50" height="50" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="48" fill="url(#wizardGrad)"/>
    <path d="M50 15 L55 40 L70 25 L58 45 L85 50 L58 55 L70 75 L55 60 L50 85 L45 60 L30 75 L42 55 L15 50 L42 45 L30 25 L45 40 Z" fill="#F5A623"/>
    <circle cx="50" cy="50" r="15" fill="#2196DF"/>
    <path d="M45 47 L48 53 L55 47" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
    <defs>
      <linearGradient id="wizardGrad" x1="0" y1="0" x2="100" y2="100">
        <stop offset="0%" stopColor="#E8F4FC"/>
        <stop offset="100%" stopColor="#C5E4F7"/>
      </linearGradient>
    </defs>
  </svg>
);

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { unreadCount } = useSelector((state) => state.notifications);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/');
  };

  const getDashboardLink = () => user?.role === 'employer' ? '/employer/dashboard' : '/dashboard';

  return (
    <nav className={`navbar navbar-expand-lg fixed-top ${isScrolled ? 'scrolled' : ''}`}>
      <div className="container">
        <Link to="/" className="navbar-brand d-flex align-items-center gap-2">
          <WizardLogo />
          <span>
            <span className="get-jobs">GetJobs</span>
            <span className="wiz">Wiz</span>
            <span className="dot-com">.com</span>
          </span>
        </Link>

        <button className="navbar-toggler border-0" type="button" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
        </button>

        <div className={`collapse navbar-collapse ${isMobileMenuOpen ? 'show' : ''}`}>
          <ul className="navbar-nav mx-auto mb-2 mb-lg-0">
            <li className="nav-item">
              <NavLink to="/" className="nav-link">Home</NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="/jobs" className="nav-link">Browse Jobs</NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="/companies" className="nav-link">Companies</NavLink>
            </li>
            <li className="nav-item">
              <NavLink to="/how-it-works" className="nav-link">How It Works</NavLink>
            </li>
          </ul>

          <div className="d-flex align-items-center gap-3">
            {isAuthenticated ? (
              <>
                <Link to="/notifications" className="btn btn-ghost btn-icon position-relative">
                  <FiBell size={20} />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                </Link>

                <div className="dropdown">
                  <button
                    className="btn btn-ghost d-flex align-items-center gap-2"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <div className="avatar avatar-sm">
                      {user?.avatar?.url ? (
                        <img src={user.avatar.url} alt={user.firstName} />
                      ) : (
                        <span>{user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}</span>
                      )}
                    </div>
                    <span className="d-none d-lg-inline">{user?.firstName}</span>
                  </button>

                  {isDropdownOpen && (
                    <div className="dropdown-menu show">
                      <div className="dropdown-header px-3 py-2">
                        <p className="mb-0 fw-semibold">{user?.firstName} {user?.lastName}</p>
                        <small className="text-muted">{user?.email}</small>
                      </div>
                      <div className="dropdown-divider"></div>
                      <Link to={getDashboardLink()} className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                        <FiBriefcase className="me-2" /> Dashboard
                      </Link>
                      {user?.role === 'jobseeker' && (
                        <>
                          <Link to="/my-applications" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                            <FiBriefcase className="me-2" /> My Applications
                          </Link>
                          <Link to="/saved-jobs" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                            <FiHeart className="me-2" /> Saved Jobs
                          </Link>
                        </>
                      )}
                      <Link to="/profile" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                        <FiUser className="me-2" /> Profile
                      </Link>
                      <Link to="/settings" className="dropdown-item" onClick={() => setIsDropdownOpen(false)}>
                        <FiSettings className="me-2" /> Settings
                      </Link>
                      <div className="dropdown-divider"></div>
                      <button className="dropdown-item text-danger" onClick={handleLogout}>
                        <FiLogOut className="me-2" /> Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/employer/post-job" className="btn btn-primary">Post a Job</Link>
                <Link to="/login" className="btn btn-outline-primary">
                  <FiUser className="me-1" /> Log In
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
